// =============================================================================
// Resource Cleanup - 资源清理组件
// =============================================================================

import React, { useState, useEffect } from 'react';
import {
  Trash2,
  AlertTriangle,
  Clock,
  HardDrive,
  Activity,
  RefreshCw,
  CheckCircle,
  XCircle,
  Play
} from 'lucide-react';
import toast from 'react-hot-toast';

export default function ResourceCleanup({ workspaceId }) {
  const [cleanupStats, setCleanupStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cleaning, setCleaning] = useState(false);
  const [cleanupResults, setCleanupResults] = useState(null);

  useEffect(() => {
    loadCleanupStats();
  }, [workspaceId]);

  const loadCleanupStats = async () => {
    try {
      setLoading(true);
      
      // 获取终端资源统计
      const terminalResponse = await fetch('/api/terminals/stats/resources', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      // 获取脚本执行统计
      const scriptResponse = await fetch('/api/scripts/stats', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      const terminalData = await terminalResponse.json();
      const scriptData = await scriptResponse.json();

      if (terminalData.success && scriptData.success) {
        setCleanupStats({
          terminals: terminalData.data.stats,
          scripts: scriptData.data.stats
        });
      }
    } catch (error) {
      console.error('Failed to load cleanup stats:', error);
      toast.error('加载清理统计失败');
    } finally {
      setLoading(false);
    }
  };

  const performCleanup = async (options = {}) => {
    const {
      cleanIdleTerminals = false,
      cleanOldExecutions = false,
      cleanBuffers = false,
      forceClean = false
    } = options;

    if (!cleanIdleTerminals && !cleanOldExecutions && !cleanBuffers) {
      toast.error('请选择至少一个清理选项');
      return;
    }

    setCleaning(true);
    setCleanupResults(null);

    try {
      const results = {
        terminalsDeleted: 0,
        executionsDeleted: 0,
        buffersCleared: 0,
        errors: []
      };

      // 清理空闲终端
      if (cleanIdleTerminals) {
        try {
          // 获取空闲终端列表
          const response = await fetch(`/api/terminals/workspace/${workspaceId}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              const idleTerminals = data.data.filter(terminal => {
                const lastActivity = new Date(terminal.lastActivity);
                const now = new Date();
                const idleTime = (now - lastActivity) / (1000 * 60); // 分钟
                return idleTime > 30 && terminal.subscribers === 0; // 30分钟无活动且无连接
              });

              if (idleTerminals.length > 0) {
                const terminalIds = idleTerminals.map(t => t.id);
                const deleteResponse = await fetch('/api/terminals', {
                  method: 'DELETE',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                  },
                  body: JSON.stringify({
                    terminalIds,
                    force: forceClean
                  })
                });

                if (deleteResponse.ok) {
                  const deleteData = await deleteResponse.json();
                  results.terminalsDeleted = deleteData.data.success;
                  if (deleteData.data.failed > 0) {
                    results.errors.push(`${deleteData.data.failed} 个终端删除失败`);
                  }
                }
              }
            }
          }
        } catch (error) {
          results.errors.push(`清理终端失败: ${error.message}`);
        }
      }

      // 清理旧的脚本执行记录
      if (cleanOldExecutions) {
        try {
          // 这里需要后端提供清理API
          // 暂时跳过，因为我们还没有实现这个API
          console.log('Script execution cleanup not implemented yet');
        } catch (error) {
          results.errors.push(`清理脚本执行记录失败: ${error.message}`);
        }
      }

      // 清理缓冲区
      if (cleanBuffers) {
        try {
          // 这里需要后端提供缓冲区清理API
          // 暂时跳过
          console.log('Buffer cleanup not implemented yet');
        } catch (error) {
          results.errors.push(`清理缓冲区失败: ${error.message}`);
        }
      }

      setCleanupResults(results);
      
      // 显示结果
      if (results.terminalsDeleted > 0) {
        toast.success(`清理完成：删除了 ${results.terminalsDeleted} 个空闲终端`);
      }
      
      if (results.errors.length > 0) {
        results.errors.forEach(error => toast.error(error));
      }

      if (results.terminalsDeleted === 0 && results.errors.length === 0) {
        toast.info('没有需要清理的资源');
      }

      // 重新加载统计
      await loadCleanupStats();

    } catch (error) {
      console.error('Cleanup failed:', error);
      toast.error(`清理失败: ${error.message}`);
    } finally {
      setCleaning(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDuration = (ms) => {
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-gray-600">加载清理统计...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-800">资源清理</h3>
        <button
          onClick={loadCleanupStats}
          disabled={loading}
          className="p-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* 资源统计 */}
      {cleanupStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* 终端资源 */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-3">
              <Activity className="w-5 h-5 text-blue-500" />
              <h4 className="font-medium text-gray-800">终端资源</h4>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">总终端数:</span>
                <span className="font-medium">{cleanupStats.terminals.totalTerminals}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">活跃连接:</span>
                <span className="font-medium">{cleanupStats.terminals.totalConnections}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">缓冲区大小:</span>
                <span className="font-medium">
                  {formatBytes(cleanupStats.terminals.memoryUsage.totalBufferSize)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">历史命令:</span>
                <span className="font-medium">
                  {cleanupStats.terminals.memoryUsage.totalHistoryCommands}
                </span>
              </div>
            </div>
          </div>

          {/* 脚本执行 */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-3">
              <Play className="w-5 h-5 text-green-500" />
              <h4 className="font-medium text-gray-800">脚本执行</h4>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">总执行数:</span>
                <span className="font-medium">{cleanupStats.scripts.total}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">运行中:</span>
                <span className="font-medium text-blue-600">{cleanupStats.scripts.running}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">已完成:</span>
                <span className="font-medium text-green-600">{cleanupStats.scripts.completed}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">失败:</span>
                <span className="font-medium text-red-600">{cleanupStats.scripts.failed}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 清理选项 */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="font-medium text-gray-800 mb-4">清理选项</h4>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
            <div>
              <div className="font-medium text-gray-800">清理空闲终端</div>
              <div className="text-sm text-gray-600">删除30分钟无活动且无连接的终端</div>
            </div>
            <button
              onClick={() => performCleanup({ cleanIdleTerminals: true })}
              disabled={cleaning}
              className="px-4 py-2 bg-orange-100 text-orange-700 rounded hover:bg-orange-200 disabled:opacity-50"
            >
              {cleaning ? '清理中...' : '清理'}
            </button>
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
            <div>
              <div className="font-medium text-gray-800">清理旧执行记录</div>
              <div className="text-sm text-gray-600">删除24小时前的脚本执行记录</div>
            </div>
            <button
              onClick={() => performCleanup({ cleanOldExecutions: true })}
              disabled={cleaning}
              className="px-4 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50"
            >
              {cleaning ? '清理中...' : '清理'}
            </button>
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
            <div>
              <div className="font-medium text-gray-800">清理终端缓冲区</div>
              <div className="text-sm text-gray-600">清空所有终端的输出缓冲区</div>
            </div>
            <button
              onClick={() => performCleanup({ cleanBuffers: true })}
              disabled={cleaning}
              className="px-4 py-2 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 disabled:opacity-50"
            >
              {cleaning ? '清理中...' : '清理'}
            </button>
          </div>

          <div className="border-t pt-4">
            <button
              onClick={() => performCleanup({ 
                cleanIdleTerminals: true, 
                cleanOldExecutions: true, 
                cleanBuffers: true 
              })}
              disabled={cleaning}
              className="w-full px-4 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50 font-medium"
            >
              {cleaning ? '全面清理中...' : '全面清理'}
            </button>
          </div>
        </div>
      </div>

      {/* 清理结果 */}
      {cleanupResults && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-800 mb-4">清理结果</h4>
          
          <div className="space-y-2">
            {cleanupResults.terminalsDeleted > 0 && (
              <div className="flex items-center space-x-2 text-green-600">
                <CheckCircle className="w-4 h-4" />
                <span>删除了 {cleanupResults.terminalsDeleted} 个空闲终端</span>
              </div>
            )}
            
            {cleanupResults.executionsDeleted > 0 && (
              <div className="flex items-center space-x-2 text-green-600">
                <CheckCircle className="w-4 h-4" />
                <span>删除了 {cleanupResults.executionsDeleted} 个执行记录</span>
              </div>
            )}
            
            {cleanupResults.buffersCleared > 0 && (
              <div className="flex items-center space-x-2 text-green-600">
                <CheckCircle className="w-4 h-4" />
                <span>清理了 {cleanupResults.buffersCleared} 个缓冲区</span>
              </div>
            )}
            
            {cleanupResults.errors.map((error, index) => (
              <div key={index} className="flex items-center space-x-2 text-red-600">
                <XCircle className="w-4 h-4" />
                <span>{error}</span>
              </div>
            ))}
            
            {cleanupResults.terminalsDeleted === 0 && 
             cleanupResults.executionsDeleted === 0 && 
             cleanupResults.buffersCleared === 0 && 
             cleanupResults.errors.length === 0 && (
              <div className="flex items-center space-x-2 text-gray-600">
                <AlertTriangle className="w-4 h-4" />
                <span>没有需要清理的资源</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
